import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import {
  fetchDetectionChains,
  fetchDetectionChain,
  enableDetectionChain,
  disableDetectionChain,
  createDetectionChain,
  updateDetectionChain,
  deleteDetectionChain
} from "./api/detectionChainsAPI";
const detectionChainsListKey = (params) => ["detection-chains", "list", params];
const detectionChainDetailKey = (name) => ["detection-chains", "detail", name];
export const buildBulkActionMessage = (verb, chainName, counts) => {
  const total = counts.affected + counts.alreadyInTargetState + counts.skipped + counts.failed;
  const skippedNote = counts.skipped ? ` (${counts.skipped} skipped \u2014 no edit access)` : "";
  const failedNote = counts.failed ? ` (${counts.failed} failed)` : "";
  return `${verb} ${counts.affected} of ${total} event definitions for chain "${chainName}"${skippedNote}${failedNote}`;
};
export const buildBulkActionNotification = (verb, chainName, counts) => {
  const message = buildBulkActionMessage(verb, chainName, counts);
  const presentVerb = verb === "Enabled" ? "enable" : "disable";
  const blocked = counts.skipped > 0 || counts.failed > 0;
  const permissionHint = counts.skipped ? " Verify you have edit access to the chain\u2019s event definitions." : "";
  if (counts.affected === 0 && blocked) {
    return { severity: "error", title: `Could not ${presentVerb} chain`, message: `${message}.${permissionHint}` };
  }
  if (blocked) {
    return {
      severity: "error",
      title: `Chain partially ${verb.toLowerCase()}`,
      message: `${message}.${permissionHint}`
    };
  }
  return { severity: "success", title: `Chain ${verb.toLowerCase()}`, message };
};
export const useGetDetectionChains = (params) => useQuery({
  queryKey: detectionChainsListKey(params),
  queryFn: () => fetchDetectionChains(params),
  placeholderData: keepPreviousData
});
export const useGetDetectionChain = (name) => useQuery({
  queryKey: detectionChainDetailKey(name != null ? name : ""),
  queryFn: () => fetchDetectionChain(name),
  enabled: !!name
});
export const useEnableDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name) => enableDetectionChain(name),
    onSuccess: (response, name) => {
      const { severity, title, message } = buildBulkActionNotification("Enabled", name, {
        affected: response.enabled_count,
        alreadyInTargetState: response.already_enabled_count,
        skipped: response.skipped_count,
        failed: response.failed_count
      });
      UserNotification[severity](message, title);
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, name) => {
      UserNotification.error(`Failed to enable chain "${name}": ${error.message}`, "Enable chain failed");
    }
  });
};
export const useCreateDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createDetectionChain(payload),
    onSuccess: (chain) => {
      UserNotification.success(`Detection chain "${chain.name}" created`, "Chain created");
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error) => {
      UserNotification.error(`Failed to create detection chain: ${error.message}`, "Create chain failed");
    }
  });
};
export const useUpdateDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateDetectionChain(id, payload),
    onSuccess: (chain) => {
      UserNotification.success(`Detection chain "${chain.name}" updated`, "Chain updated");
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error) => {
      UserNotification.error(`Failed to update detection chain: ${error.message}`, "Update chain failed");
    }
  });
};
export const useDeleteDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => deleteDetectionChain(id),
    onSuccess: (_response, { name }) => {
      UserNotification.success(`Detection chain "${name}" deleted`, "Chain deleted");
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, { name }) => {
      UserNotification.error(`Failed to delete chain "${name}": ${error.message}`, "Delete chain failed");
    }
  });
};
export const useDisableDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name) => disableDetectionChain(name),
    onSuccess: (response, name) => {
      const { severity, title, message } = buildBulkActionNotification("Disabled", name, {
        affected: response.disabled_count,
        alreadyInTargetState: response.already_disabled_count,
        skipped: response.skipped_count,
        failed: response.failed_count
      });
      UserNotification[severity](message, title);
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, name) => {
      UserNotification.error(`Failed to disable chain "${name}": ${error.message}`, "Disable chain failed");
    }
  });
};
